/**
 * Short texts for the grid of tables: one cell each, read at a glance. The
 * inspector (Task 14) shows the full settings; these only summarise them.
 */
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { getTableOperatorLabel } from "utils/codebookUtils"
import { meanSummaryStats, showsSiblings } from "../../compiler/table"
import { conditionPaths } from "../../model/conditions"
import { isYesNoField } from "../../model/dataVariables"
import type { ResolvedTable } from "../../model/resolve"
import { RAW_NAMES, ShownNames } from "../../model/shownLabels"
import { inSlice, summaryName } from "../../model/summaries"
import type { DisplayStatus } from "../../runtime/tabSpecStore"
import type { BaseRule, SortRule, TableKind, TableSpec } from "../../types/tabSpecDocument"

export const STATUS_TEXT: Record<DisplayStatus, string> = {
  idle: "Not run",
  queued: "Waiting",
  running: "Running",
  done: "Ready",
  error: "Failed",
  skipped: "Skipped",
  stale: "Out of date",
  excluded: "Excluded",
}

export const KIND_TEXT: Record<TableKind, string> = {
  categorical: "Single choice",
  multi: "Multiple choice",
  scale: "Scale",
  numeric: "Number",
  "grid-summary": "Grid summary",
  text: "Text",
}

export const BASE_TEXT: Record<BaseRule, string> = {
  asked: "Asked",
  answered: "Answered",
  all: "All respondents",
}

/** The base rules as a choice: the bulk bar and the defaults. */
export const BASE_CHOICE_TEXT: Record<BaseRule, string> = {
  asked: "Who was asked",
  answered: "Who answered",
  all: "All respondents",
}

/**
 * The orders of the rows as a choice: the inspector and the defaults. "none"
 * is the order the rows follow: the questionnaire's, or the data's
 * (`fromData`).
 */
export const sortChoiceText = (rule: SortRule, fromData = false): string =>
  rule === "desc" ? "Highest first" : fromData ? "As in the data" : "As in the questionnaire"

/**
 * What the Type column says of a table: its kind, and for a grid summary
 * what each grid row shows ("Summary: Top 2", "Summary: Mean"), an answer
 * as the tables show it (`names`).
 */
export const describeKind = (resolved: ResolvedTable, names: ShownNames = RAW_NAMES): string => {
  const { table, summary } = resolved
  if (table.kind !== "grid-summary" || !summary) return KIND_TEXT[table.kind]
  return `Summary: ${summaryName(summary, (label) => names.option(table.question, label))}`
}

/** Every field a table can override; clearing them all brings back the defaults. */
export const OVERRIDE_FIELDS: (keyof TableSpec)[] = [
  "title",
  "bannerId",
  "base",
  "baseText",
  "significance",
  "statistics",
  "meanStats",
  "note",
  "rows",
  "values",
  "scores",
  "hiddenRows",
  "sort",
  "hideEmpty",
]

const plural = (n: number, word: string): string =>
  `${n} ${word}${n === 1 ? "" : "s"}`

/** "a", "a and b", "a, b and c". */
const andList = (items: string[]): string =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`

/**
 * The rows of a table in a few words: the options shown ("5 of 6 options"
 * when some are hidden), then each net, difference and the mean by name. A
 * grid summary gives its grid rows (those of its slice, without the hidden
 * ones) and what it shows of each ("3 rows, top 2", "3 rows, mean and
 * standard error"). A lone yes or no field of the data gives "Yes, No". A numeric table gives
 * "Each value" and its ranges ("Each value, 3 ranges"), "" with neither; a
 * text table has no rows of its own: "". A grid of the data shown as one table
 * (`showsSiblings`) gives its grid rows and how: "Stacked, 4 rows: 3
 * options, NPS", or one row each, "4 rows, NPS", the row chosen named as the
 * table shows it (`names`).
 */
export const describeRows = (resolved: ResolvedTable, names: ShownNames = RAW_NAMES): string => {
  const q = resolved.question
  const { table, summary } = resolved
  if (table.kind === "grid-summary") {
    const hidden = new Set(table.hiddenRows ?? [])
    const count = (q?.instances ?? []).filter((i) => inSlice(i, table.slice) && !hidden.has(i.id)).length
    const shows =
      summary?.kind === "mean"
        ? andList(meanSummaryStats(resolved.statistics).map((s) => s.display.toLowerCase()))
        : summary?.kind === "top" || summary?.kind === "bottom" || summary?.kind === "middle"
          ? summaryName(summary).toLowerCase()
          : summary
            ? summaryName(summary, (label) => names.option(table.question, label))
            : ""
    return `${plural(count, "row")}, ${shows}`
  }
  // A number table: each value as a row, then its ranges.
  if (resolved.table.kind === "numeric") {
    const ranges = resolved.rows.filter((r) => r.kind === "range").length
    return [
      ...(resolved.table.values ? ["Each value"] : []),
      ...(ranges > 0 ? [plural(ranges, "range")] : []),
    ].join(", ")
  }
  // One yes or no field of the data: the compiler always gives it these rows.
  if (q && isYesNoField(q)) return "Yes, No"
  const options = resolved.rows.filter((r) => r.kind === "option")
  const shown = options.filter((r) => !r.hidden).length
  const parts: string[] = []
  if (options.length > 0)
    parts.push(
      shown === options.length
        ? plural(options.length, "option")
        : `${shown} of ${plural(options.length, "option")}`
    )
  resolved.rows.forEach((r) => {
    if (r.kind === "net" || r.kind === "difference") parts.push(r.name)
    if (r.kind === "mean") parts.push("Mean")
  })
  if (!q || !showsSiblings(resolved.table, q)) return parts.join(", ")
  const count = plural(q.instances.length, "row")
  if (resolved.table.siblingLayout !== "row")
    return parts.length > 0 ? `Stacked, ${count}: ${parts.join(", ")}` : `Stacked, ${count}`
  const chosen = resolved.table.siblingRow?.trim() ?? ""
  if (!chosen) return `${count}, no row chosen`
  // An option is stored by its label: it shows its rename, or its shown label.
  const option = options.find((r) => r.kind === "option" && r.label === chosen)
  const rename = option?.kind === "option" ? option.rename?.trim() : undefined
  return `${count}, ${option ? rename || names.option(q.label, chosen) : chosen}`
}

/** A table from the data has no routing: "asked" runs on who answered (compileBase). */
export const describeBase = (resolved: ResolvedTable): string =>
  BASE_TEXT[resolved.table.source === "data" && resolved.base === "asked" ? "answered" : resolved.base]

/** What the compiler shows when a table's list of statistics is empty. */
const FALLBACK_CHOICE = [STATISTIC_TYPE.COL_PERCENT, STATISTIC_TYPE.N]
const FALLBACK_NUMERIC = [
  STATISTIC_TYPE.AVG,
  STATISTIC_TYPE.MIN,
  STATISTIC_TYPE.MAX,
  STATISTIC_TYPE.BASE_N,
]

/**
 * The statistics a table shows: none for a text table, which is never
 * tabulated; the compiler's fallback (`fallback: true`) when the list is
 * empty, Column % and Count for a choice table, Mean, Minimum, Maximum and
 * Base for a numeric one.
 */
export const shownStatistics = (
  resolved: ResolvedTable
): { statistics: STATISTIC_TYPE[]; fallback: boolean } => {
  if (resolved.table.kind === "text") return { statistics: [], fallback: false }
  // A grid summary that is no summary of means shows a share (a sum) per
  // grid row, and the row's base when its list names it.
  const { summary } = resolved
  if (summary && summary.kind !== "mean") {
    const shown = summary.kind === "sum" ? STATISTIC_TYPE.SUM : STATISTIC_TYPE.PERC
    const base = resolved.statistics.includes(STATISTIC_TYPE.BASE_N) ? [STATISTIC_TYPE.BASE_N] : []
    return { statistics: [shown, ...base], fallback: false }
  }
  if (resolved.statistics.length > 0)
    return { statistics: resolved.statistics, fallback: false }
  return {
    statistics: resolved.table.kind === "numeric" ? FALLBACK_NUMERIC : FALLBACK_CHOICE,
    fallback: true,
  }
}

export const describeStatistics = (resolved: ResolvedTable): string =>
  shownStatistics(resolved).statistics.map(getTableOperatorLabel).join(", ")

/** "" without a filter; otherwise the number of paths it names (`conditionPaths`). */
export const describeFilterShort = (table: TableSpec): string => {
  const count = conditionPaths(table.filter).length
  return count === 0 ? "" : plural(count, "condition")
}

/**
 * Whether the table sets `field` itself rather than following the defaults:
 * `rows` and `scores` are null while they follow the defaults, every other
 * override is absent.
 */
export const isOverridden = (
  table: TableSpec,
  field: keyof TableSpec
): boolean =>
  field === "rows" || field === "scores"
    ? table[field] !== null
    : table[field] !== undefined
